import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { BaseExternalCellRendererComponent } from '../base_external_cell_renderer.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ElementRef } from '@angular/core';
import { BoolPropertyViewModel } from '../../../../../src/lib/view-models/base-type/bool-property-view-model';
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from 'ngx-popperjs';
import { PopperHelper } from '@nts/std/utility';
import { AsyncPipe } from '@angular/common';
import { CheckboxComponent } from '../../../../../src/lib/components/shared/checkbox/checkbox.component';
import type { ExternalColumnCellRendererParams, PropertyViewModelInterface } from '@nts/std/interfaces';
import { takeUntil } from 'rxjs';


// create your cellEditor as a Angular component
@UntilDestroy()
@Component({
    selector: 'nts-external-renderer-cell',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    NgxPopperjsModule,
    CheckboxComponent,
    AsyncPipe
],
    styleUrls: [`./external-cell-renderer.component.scss`],
    template: `
        <span>
          @if (!params?.data) {
            <div class="lds-ring">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          }
        </span>
        @if (params?.data) {
          <div class="read-template">
            <div class="renderer-cell"
              #popperError="popper"
              [popper]="tooltipErrTemplate"
              popperAppendTo="body"
              popperApplyClass="error"
              [popperPreventOverflow]="false"
              [popperHideOnScroll]="true"
              [popperDisabled]="(externalPropertyViewModel.errors$ | async).length == 0"
              [popperTrigger]="ngxPopperjsTriggers.hover"
              [popperPlacement]="ngxPopperjsPlacements.TOP"
              [style.width.px]="boolPropertyViewModel ? 32 : null">
              @if (boolPropertyViewModel) {
                <nts-checkbox [(value)]="displayValue" [title]="boolPropertyViewModel.metadataDescription" [isDisabled]="true" ></nts-checkbox>
              }
              @if (!boolPropertyViewModel) {
                <div [class.numeric-alignment]="isNumeric" class="cell-render-content">{{ displayValue }}</div>
              }
            </div>
            <popper-content #tooltipErrTemplate>
              @for (item of externalPropertyViewModel.errors$ | async; track item) {
                <div>
                  {{ item }}
                </div>
              }
            </popper-content>
          </div>
        }
        `
})
export class ExternalCellRendererComponent extends BaseExternalCellRendererComponent {

  @ViewChild('popperError', { static: false }) popperError: NgxPopperjsDirective;

  ngxPopperjsTriggers = NgxPopperjsTriggers;
  ngxPopperjsPlacements = NgxPopperjsPlacements;
  displayValue;
  boolPropertyViewModel: BoolPropertyViewModel = null;
  isNumeric = false;

  constructor(
    private readonly cd: ChangeDetectorRef,
    public readonly el: ElementRef
  ) {
    super();
  }

  override agInit(params: ExternalColumnCellRendererParams): void {
    super.agInit(params);


    this.externalPropertyViewModel.externalDomainModelChanged.pipe(
      untilDestroyed(this),
      takeUntil(params.columnInfo.destroy$)
    ).subscribe(() => {
      this.checkDisplayValue();
      this.checkPopper();
      this.cd.detectChanges();
    });

    this.externalPropertyViewModel.decodeCompleted.pipe(
      untilDestroyed(this),
      takeUntil(params.columnInfo.destroy$)
    ).subscribe(() => {
      this.checkDisplayValue();
      this.checkPopper();
      this.cd.detectChanges();
    });

    this.externalPropertyViewModel.onErrorStatusChanged.pipe(
      untilDestroyed(this),
      takeUntil(params.columnInfo.destroy$)
    ).subscribe(() => {
      this.params.api.refreshCells({ force: true });
      this.checkDisplayValue();
      this.checkPopper();
      this.cd.detectChanges();
    });

    this.externalPropertyViewModel.onFocusRequested.pipe(
      untilDestroyed(this),
      takeUntil(params.columnInfo.destroy$)
    ).subscribe(() => {
      this.el.nativeElement.parentElement.parentElement.scrollIntoView();

      if (this.externalPropertyViewModel.errors$.value?.length > 0) {
        PopperHelper.show(this.popperError);
      }
    });

    this.checkDisplayValue();

    this.cd.detectChanges();
  }

  get description(): PropertyViewModelInterface {
    return this.externalPropertyViewModel.descriptionProperties.values().next().value;
  }

  checkDisplayValue() {
    const params = this.params as ExternalColumnCellRendererParams;

    this.displayValue = this.externalPropertyViewModel.getDescription(
      params.columnInfo.showCodeInDescription,

      // se si passa null viene considerato quello definito nel external view model, quindi se viene passato un array vuoto viene considerato come se fosse passato null
      params.columnInfo.decodeProperties?.length > 0 ? params.columnInfo.decodeProperties : null,
      params.columnInfo.fieldSeparator
    );
  }

  private checkPopper() {
    if (
      this.popperError
      && (
        this.externalPropertyViewModel.errors$.value == null
        || this.externalPropertyViewModel.errors$.value.length === 0
      )
    ) {
      PopperHelper.hide(this.popperError);
    }
  }
}
